/* eslint-disable @typescript-eslint/no-explicit-any */
import { useMemo, useState } from 'react'
import { Card, CardContent, CardHeader } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { toast } from 'sonner'
import Loading from '@/components/Loading'
import type { PlatformAdminRow, PlatformRole } from '@/types/platform'
import {
  useCreatePlatformAdminMutation,
  useDisablePlatformAdminMutation,
  usePlatformAdminsQuery,
  useUpdatePlatformAdminRoleMutation,
} from '@/hooks/usePlatformAdmins'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { z } from 'zod'
import { zodResolver } from '@hookform/resolvers/zod'
import { useForm } from 'react-hook-form'
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form'
import { PasswordInput } from '@/components/PasswordInput'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'

const roles: PlatformRole[] = ['SUPER_ADMIN', 'BILLING', 'SUPPORT', 'OPS']

const createSchema = z.object({
  name: z.string().min(2, 'Nom trop court'),
  email: z.email('Email invalide'),
  password: z.string().min(8, 'Min 8 caractères'),
  role: z.enum(['SUPER_ADMIN', 'BILLING', 'SUPPORT', 'OPS']),
})
type CreateValues = z.infer<typeof createSchema>

export default function PlatformAdminsPage() {
  const { data, status } = usePlatformAdminsQuery()
  const createMut = useCreatePlatformAdminMutation()

  const [q, setQ] = useState('')
  const [openCreate, setOpenCreate] = useState(false)
  const [selected, setSelected] = useState<PlatformAdminRow | null>(null)

  const items = useMemo(() => {
    const rows = data?.items ?? []
    const query = q.trim().toLowerCase()
    if (!query) return rows
    return rows.filter((a) => {
      return (
        a.email.toLowerCase().includes(query) ||
        a.name.toLowerCase().includes(query) ||
        a.role.toLowerCase().includes(query)
      )
    })
  }, [data, q])

  const form = useForm<CreateValues>({
    resolver: zodResolver(createSchema),
    defaultValues: { name: '', email: '', password: '', role: 'SUPPORT' },
  })

  const onCreate = async (v: CreateValues) => {
    try {
      await createMut.mutateAsync({
        name: v.name.trim(),
        email: v.email.trim().toLowerCase(),
        password: v.password,
        role: v.role,
      })
      toast.success('Admin créé')
      setOpenCreate(false)
      form.reset()
    } catch (e: any) {
      toast.error('Création impossible', {
        description: e?.response?.data?.message ?? 'Erreur inconnue',
      })
    }
  }

  if (status === 'pending') return <Loading />

  return (
    <>
      <Card>
        <CardHeader>
          <div className='flex items-center justify-between gap-3'>
            <div>
              <h2 className='text-xl font-semibold'>Platform Admins</h2>
              <p className='text-sm text-muted-foreground'>
                Créer / changer rôle / désactiver.
              </p>
            </div>
            <Button onClick={() => setOpenCreate(true)}>Nouvel admin</Button>
          </div>
        </CardHeader>
        <CardContent>
          <div className='flex items-center gap-2'>
            <Input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder='Recherche: email, nom, role...'
            />
          </div>

          <div className='mt-4 space-y-2'>
            {items.map((a) => (
              <AdminRow key={a.id} row={a} onOpen={() => setSelected(a)} />
            ))}
            {items.length === 0 && (
              <div className='text-sm text-muted-foreground'>
                Aucun admin trouvé
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Create modal */}
      <Dialog open={openCreate} onOpenChange={setOpenCreate}>
        <DialogContent className='sm:max-w-[520px]'>
          <DialogHeader>
            <DialogTitle>Créer un admin platform</DialogTitle>
          </DialogHeader>

          <Form {...form}>
            <form onSubmit={form.handleSubmit(onCreate)} className='space-y-4'>
              <FormField
                control={form.control}
                name='name'
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Nom</FormLabel>
                    <FormControl>
                      <Input placeholder='Ex: Support 1' {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name='email'
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Email</FormLabel>
                    <FormControl>
                      <Input placeholder='support@mogesapp.com' {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name='password'
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Mot de passe</FormLabel>
                    <FormControl>
                      <PasswordInput
                        placeholder='Min 8 caractères'
                        {...field}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name='role'
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Rôle</FormLabel>
                    <Select value={field.value} onValueChange={field.onChange}>
                      <FormControl>
                        <SelectTrigger>
                          <SelectValue placeholder='Choisir un rôle' />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        {roles.map((r) => (
                          <SelectItem key={r} value={r}>
                            {r}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <div className='flex justify-end gap-2 pt-2'>
                <Button
                  type='button'
                  variant='outline'
                  onClick={() => setOpenCreate(false)}
                >
                  Annuler
                </Button>
                <Button type='submit' disabled={createMut.isPending}>
                  {createMut.isPending ? 'Création…' : 'Créer'}
                </Button>
              </div>
            </form>
          </Form>
        </DialogContent>
      </Dialog>

      {/* Edit modal */}
      {selected && (
        <AdminEditDialog row={selected} onClose={() => setSelected(null)} />
      )}
    </>
  )
}

function AdminRow({
  row,
  onOpen,
}: {
  row: PlatformAdminRow
  onOpen: () => void
}) {
  return (
    <div className='flex items-center justify-between rounded-lg border p-3'>
      <div>
        <div className='font-medium'>{row.name}</div>
        <div className='text-xs text-muted-foreground'>{row.email}</div>
      </div>
      <div className='text-right'>
        <div className='text-sm'>
          {row.role} • {row.active ? 'active' : 'disabled'}
        </div>
        <Button variant='outline' size='sm' onClick={onOpen}>
          Gérer
        </Button>
      </div>
    </div>
  )
}

function AdminEditDialog({
  row,
  onClose,
}: {
  row: PlatformAdminRow
  onClose: () => void
}) {
  const roleMut = useUpdatePlatformAdminRoleMutation(row.id)
  const disableMut = useDisablePlatformAdminMutation(row.id)

  const [role, setRole] = useState<PlatformRole>(row.role)

  const saveRole = async () => {
    try {
      await roleMut.mutateAsync({ role })
      toast.success('Rôle mis à jour')
      onClose()
    } catch (e: any) {
      toast.error('Update role impossible', {
        description: e?.response?.data?.message ?? 'Erreur inconnue',
      })
    }
  }

  const toggleActive = async () => {
    try {
      await disableMut.mutateAsync({ active: !row.active })
      toast.success(row.active ? 'Admin désactivé' : 'Admin réactivé')
      onClose()
    } catch (e: any) {
      toast.error('Opération impossible', {
        description: e?.response?.data?.message ?? 'Erreur inconnue',
      })
    }
  }

  return (
    <Dialog open onOpenChange={() => onClose()}>
      <DialogContent className='sm:max-w-[520px]'>
        <DialogHeader>
          <DialogTitle>Gérer admin</DialogTitle>
        </DialogHeader>

        <div className='space-y-3'>
          <div className='rounded-lg border p-3'>
            <div className='font-medium'>{row.name}</div>
            <div className='text-xs text-muted-foreground'>{row.email}</div>
          </div>

          <div className='space-y-2'>
            <div className='text-sm font-medium'>Rôle</div>
            <Select
              value={role}
              onValueChange={(v) => setRole(v as PlatformRole)}
            >
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {roles.map((r) => (
                  <SelectItem key={r} value={r}>
                    {r}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>

            <div className='flex justify-end gap-2 pt-2'>
              <Button
                variant='outline'
                onClick={toggleActive}
                disabled={disableMut.isPending}
              >
                {row.active ? 'Désactiver' : 'Réactiver'}
              </Button>
              <Button onClick={saveRole} disabled={roleMut.isPending}>
                {roleMut.isPending ? 'Sauvegarde…' : 'Sauver rôle'}
              </Button>
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  )
}
